import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { findWhere } from "underscore";
import { useAtomValue } from "jotai";
import pluralize from "pluralize";

import { useUserRecordActivityUpdate } from "../../../components/Records/References/ReferencesComponents/SelectableReferences/useUpdateUserRecordActivity";
import { handleUpdateUserRecordRecommendedActivity } from "../../../utils/RecordUtils";
import {
  callFunctionIfExists,
  getErrorMessage,
} from "../../../utils/GeneralUtils";
import { tenantIDAtom } from "../../../atoms/GlobalAtoms";
import { Module } from "../../../types/TenantInterfaces";
import { newToastEvent } from "../../../utils/ToastUtil";
import useWorkspace from "../workspaces/useWorkspace";
import RightArrow from "../../../elements/RightArrow";
import TenantAPI from "../../api/TenantAPI";

const useCreateRecord = () => {
  const queryClient = useQueryClient();
  const tenantID = useAtomValue(tenantIDAtom);
  const { workspace } = useWorkspace();
  const { updateUserRecordActivityCache } = useUserRecordActivityUpdate();

  const createRecord = useMutation({
    mutationFn: ({
      module,
      payload,
      onCreate,
    }: {
      module: Module;
      payload: any;
      onCreate: ((r: any) => void) | undefined;
      onSuccess?: ((r: any) => void) | undefined;
      onError?: ((e: any) => void) | undefined;
      onSettled?: () => void;
    }) => {
      const wsPrefills = findWhere(workspace?.Prefills, {
        ModuleAPIName: module.APIName,
      });

      if (wsPrefills) {
        const tagPrefill = findWhere(wsPrefills.Prefills, { Field: "Tags" });

        if (tagPrefill) {
          payload.Tags = tagPrefill.Value;
        }
      }

      return TenantAPI.insertRecord(module.APIName, payload).then((data) => {
        let record = data.data.data.data;
        let userRecordModuleActivity =
          data.data.data.userRecordModuleActivity || {};
        callFunctionIfExists(onCreate, record);
        queryClient.invalidateQueries({
          queryKey: [tenantID, "records", module.APIName],
        });

        if (Object.keys(userRecordModuleActivity)?.length) {
          handleUpdateUserRecordRecommendedActivity(
            userRecordModuleActivity,
            updateUserRecordActivityCache
          );
        }

        // Notify user of successful record creation, provide link to new record.
        newToastEvent(
          "success",
          <>
            <div>
              Successfully created new {pluralize.singular(module?.Name)}
            </div>
            <div>
              <Link
                className="pl-2"
                to={`/clouds/${tenantID}/records/${module.APIName}/details/${record.id}/Summary`}
              >
                View <RightArrow></RightArrow>
              </Link>
            </div>
          </>
        );
      });
    },
    onError: (e: any, { onError }) => {
      callFunctionIfExists(onError, e);
      newToastEvent(
        "error",
        "We were unable to create the record. Please Try again."
      );
    },
    onSuccess: (data, { onSuccess }) => {
      callFunctionIfExists(onSuccess, data);
    },
    onSettled: (_, __, { onSettled }) => {
      callFunctionIfExists(onSettled);
    },
  });

  return {
    createRecord: createRecord.mutate,
    recordCreating: createRecord.isPending,
    createRecordError: createRecord.error
      ? getErrorMessage(createRecord.error)
      : null,
    clearError: () => createRecord.reset(),
  };
};

export default useCreateRecord;
